import { all, call, put, takeLatest } from 'typed-redux-saga/macro';
import {
    BACKEND_BASE_URL,
    DOCUMENTS_PATH,
    MODELS_3D_PATH,
    NODES_URL,
    SCENES_3D_PATH,
    SHARE_CODES_PATH,
} from '../../constants/api.constants';
import { apiRequestWrapper } from '../../utils/api/api.utils';
import { setIsError } from '../error/error.action';
import { setIsLoading, setProgress } from '../loading/loading.action';
import {
    approveAttachmentFailed,
    ApproveAttachmentStart,
    approveAttachmentSuccess,
    createScene3dFailed,
    CreateScene3dStart,
    createScene3dSuccessSuccess,
    rejectAttachmentFailed,
    RejectAttachmentStart,
    rejectAttachmentSuccess,
    replaceAttachmentFailed,
    ReplaceAttachmentStart,
    replaceAttachmentSuccess,
    sendShareCodeMailFailed,
    SendShareCodeMailStart,
    sendShareCodeMailSuccess,
    shareScene3dFailed,
    ShareScene3dStart,
    shareScene3dSuccess,
    uploadAttachmentFailed,
    UploadAttachmentStart,
    uploadAttachmentSuccess,
} from './attachment.action';
import { ATTACHMENT_ACTION_TYPES } from './attachment.type';

function getAttachmentUrl(attachmentType: string) {
    let url = '';
    switch (attachmentType) {
        case 'document':
            url = DOCUMENTS_PATH;
            break;
        case 'model3d':
            url = MODELS_3D_PATH;
            break;
        default:
            break;
    }
    return url;
}

export function* uploadAttachment({
    payload: { file, attachmentType, nodeId, onComplete },
}: UploadAttachmentStart) {
    yield* put(setIsLoading(true));
    try {
        if (attachmentType === 'model3d') {
            yield* put(setProgress(0));
            const chunkSize = 1024 * 1024; // 1MB
            const totalChunks = Math.ceil(file.size / chunkSize);
            const uploadId = Date.now();
            for (let index = 0; index < totalChunks; index++) {
                const start = index * chunkSize;
                const end = Math.min(file.size, (index + 1) * chunkSize);
                const chunk = file.slice(start, end);

                yield* call(
                    apiRequestWrapper,
                    `${NODES_URL}${nodeId}/${getAttachmentUrl(attachmentType)}upload/`,
                    'POST',
                    {
                        file: chunk,
                        index: index,
                        total_chunks: totalChunks,
                        upload_id: uploadId,
                        filename: file.name
                    },
                    true
                );

                const progress = Math.round(((index + 1) / totalChunks) * 100);
                yield* put(setProgress(progress));
            }
        } else {
            yield* call(
                apiRequestWrapper,
                `${NODES_URL}${nodeId}/${getAttachmentUrl(attachmentType)}upload/`,
                'POST',
                { file },
                true
            );
        }
        yield* put(uploadAttachmentSuccess());
        yield* put(setIsLoading(false));
        yield* put(setProgress(undefined));
        onComplete();
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error?.message ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';
        yield* put(uploadAttachmentFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setProgress(undefined));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* approveAttachment({
    payload: { attachmentId, attachmentType, onComplete },
}: ApproveAttachmentStart) {
    yield* put(setIsLoading(true));
    try {
        yield* call(
            apiRequestWrapper,
            `${BACKEND_BASE_URL}${getAttachmentUrl(
                attachmentType
            )}${attachmentId}/approve/`,
            'PUT',
            null
        );
        yield* put(approveAttachmentSuccess());
        yield* put(setIsLoading(false));
        onComplete();
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';
        yield* put(approveAttachmentFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* rejectAttachment({
    payload: { attachmentId, attachmentType, refusalNotes, onComplete },
}: RejectAttachmentStart) {
    yield* put(setIsLoading(true));
    try {
        yield* call(
            apiRequestWrapper,
            `${BACKEND_BASE_URL}${getAttachmentUrl(
                attachmentType
            )}${attachmentId}/reject/`,
            'PUT',
            { refusal_notes: refusalNotes }
        );
        yield* put(rejectAttachmentSuccess());
        yield* put(setIsLoading(false));
        onComplete();
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';
        yield* put(rejectAttachmentFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* replaceAttachment({
    payload: { attachmentId, attachmentType, file, onComplete },
}: ReplaceAttachmentStart) {
    yield* put(setIsLoading(true));
    try {
        if (attachmentType === 'model3d') {
            yield* put(setProgress(0));
            const chunkSize = 1024 * 1024; // 1MB
            const totalChunks = Math.ceil(file.size / chunkSize);
            const uploadId = Date.now();
            for (let index = 0; index < totalChunks; index++) {
                const start = index * chunkSize;
                const end = Math.min(file.size, (index + 1) * chunkSize);
                const chunk = file.slice(start, end);

                yield* call(
                    apiRequestWrapper,
                    `${BACKEND_BASE_URL}${getAttachmentUrl(
                        attachmentType
                    )}${attachmentId}/replace/`,
                    'PUT',
                    {
                        file: chunk,
                        index: index,
                        total_chunks: totalChunks,
                        upload_id: uploadId,
                        filename: file.name
                    },
                    true
                );

                const progress = Math.round(((index + 1) / totalChunks) * 100);
                yield* put(setProgress(progress));
            }
        } else {
            yield* call(
                apiRequestWrapper,
                `${BACKEND_BASE_URL}${getAttachmentUrl(
                    attachmentType
                )}${attachmentId}/replace/`,
                'PUT',
                { file },
                true
            );
        }
        yield* put(replaceAttachmentSuccess());
        yield* put(setIsLoading(false));
        yield* put(setProgress(undefined));
        onComplete();
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error?.message ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';
        yield* put(replaceAttachmentFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setProgress(undefined));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* createScene3d({
    payload: { model_3d, nodeId, onComplete },
}: CreateScene3dStart) {
    yield* put(setIsLoading(true));

    try {
        const url = `${NODES_URL}${nodeId}/${SCENES_3D_PATH}create/`;
        yield* call(apiRequestWrapper, url, 'POST', { model_3d }, true);
        yield* put(createScene3dSuccessSuccess());
        yield* put(setIsLoading(false));
        onComplete();
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error?.message ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';

        yield* put(createScene3dFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* shareScene3d({
    payload: { sceneId, validUntil, onComplete },
}: ShareScene3dStart) {
    yield* put(setIsLoading(true));

    try {
        const url = `${BACKEND_BASE_URL}${SCENES_3D_PATH}${sceneId}/share/`;
        const qrCodeSvg = yield* call(apiRequestWrapper, url, 'POST', { valid_until: validUntil }, true);
        yield* put(shareScene3dSuccess());
        yield* put(setIsLoading(false));
        onComplete(qrCodeSvg);
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error?.message ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';

        yield* put(shareScene3dFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* sendShareCodeMail({
    payload: { shareCode, email, onComplete },
}: SendShareCodeMailStart) {
    yield* put(setIsLoading(true));

    try {
        const url = `${BACKEND_BASE_URL}${SHARE_CODES_PATH}${shareCode}/send/`;
        yield* call(apiRequestWrapper, url, 'POST', { recipients: [email] });
        yield* put(sendShareCodeMailSuccess());
        yield* put(setIsLoading(false));
        onComplete();
    } catch (error: any) {
        const errorResponse =
            error.response?.data?.error_description ??
            error.response?.data?.error?.message ??
            error.response?.data?.error ??
            error.response?.data?.detail ??
            error.message ??
            'Errore server';

        yield* put(sendShareCodeMailFailed(errorResponse));
        yield* put(setIsLoading(false));
        yield* put(setIsError(true, errorResponse));
    }
}

export function* onUploadAttachmentStart() {
    yield* takeLatest(
        ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_START,
        uploadAttachment
    );
}

export function* onApproveAttachmentStart() {
    yield* takeLatest(
        ATTACHMENT_ACTION_TYPES.APPROVE_ATTACHMENT_START,
        approveAttachment
    );
}

export function* onRejectAttachmentStart() {
    yield* takeLatest(
        ATTACHMENT_ACTION_TYPES.REJECT_ATTACHMENT_START,
        rejectAttachment
    );
}

export function* onReplaceAttachmentStart() {
    yield* takeLatest(
        ATTACHMENT_ACTION_TYPES.REPLACE_ATTACHMENT_START,
        replaceAttachment
    );
}
export function* onCreateScene3dStart() {
    yield* takeLatest(
        ATTACHMENT_ACTION_TYPES.CREATE_SCENE_3D_START,
        createScene3d
    );
}

export function* onShareScene3dStart() {
    yield* takeLatest(
        ATTACHMENT_ACTION_TYPES.SHARE_SCENE_3D_START,
        shareScene3d
    );
}

export function* onSendShareCodeMailStart() {
    yield* takeLatest(
        ATTACHMENT_ACTION_TYPES.SEND_SHARE_CODE_MAIL_START,
        sendShareCodeMail
    );
}

export function* attachmentSaga() {
    yield* all([
        call(onUploadAttachmentStart),
        call(onApproveAttachmentStart),
        call(onRejectAttachmentStart),
        call(onReplaceAttachmentStart),
        call(onCreateScene3dStart),
        call(onShareScene3dStart),
        call(onSendShareCodeMailStart),
    ]);
}
